import React, { useCallback, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import {
  Alert, Box, Button, Checkbox, Chip, Dialog, DialogActions, DialogContent,
  DialogTitle, IconButton, MenuItem, Pagination, Skeleton, Snackbar, Stack,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TextField,
  FormControlLabel, Switch, ToggleButton, ToggleButtonGroup, Tooltip,
  Typography,
} from '@mui/material';
import { ThemeProvider } from '@mui/material/styles';
import { BsCheck2, BsEye, BsPencil, BsPlusLg, BsTrash } from 'react-icons/bs';

import axiosInstance from '../../axiosInstance';
import Fiche from '../Expert/Fiche';
import TaskDialog, { STATUTS } from './taskDialog';
import { themeExpert } from '../../theme/expert';

const VIDE = {
  title: '', title_bis: '', comment: '', review_at: '', sender: '',
  receiver: '', copy_receiver: '', hierarchy: '', public: false,
  status: 'notstart',
};

const statut = (code) => STATUTS.find((s) => s.code === code);

const aReprendre = (t) => (t.envoi_recu?.pieces || [])
  .filter((p) => p.statut === 'à reprendre').length;

const COULEUR_PIECE = { 'à reprendre': 'warning', reprise: 'success', 'écartée': 'default' };

const dateCourte = (v) => (v ? new Date(v).toLocaleDateString('fr-BE') : null);

// Où en est le lien de téléchargement envoyé avec un courriel.
const etatLien = (lien) => {
  if (!lien.valide) return { libelle: 'lien fermé', couleur: 'default' };
  if (lien.telechargements === 0) return { libelle: 'lien pas encore ouvert', couleur: 'warning' };
  return { libelle: `lien téléchargé ${lien.telechargements}×`, couleur: 'success' };
};

// Les tâches nées d'une pièce : son analyse (FILE), son ajout ou sa
// suppression (XFILE). Nombreuses, elles noient le reste.
const vientDUnDocument = (t) => ['FILE', 'XFILE'].includes(t.hierarchy);

// Le choix d'afficher ou non ces tâches suit l'utilisateur d'un dossier à
// l'autre ; sans stockage disponible, on les montre.
const CLE_DOCUMENTS = 'taches.voirDocuments';
const lireVoirDocuments = () => {
  try { return localStorage.getItem(CLE_DOCUMENTS) !== 'non'; } catch { return true; }
};
const ecrireVoirDocuments = (v) => {
  try { localStorage.setItem(CLE_DOCUMENTS, v ? 'oui' : 'non'); } catch { /* rien */ }
};

function TaskView() {
  const { expertiseId } = useParams();

  const [taches, setTaches] = useState([]);
  const [pages, setPages] = useState(1);
  const [page, setPage] = useState(1);
  const [vue, setVue] = useState('utiles');
  const [voirDocuments, setVoirDocuments] = useState(lireVoirDocuments);
  const [chargement, setChargement] = useState(true);
  const [erreur, setErreur] = useState('');
  const [message, setMessage] = useState('');

  const [modeles, setModeles] = useState([]);
  const [listes, setListes] = useState([]);
  const [listeChoisie, setListeChoisie] = useState('');
  const [contacts, setContacts] = useState([]);
  const [pieces, setPieces] = useState([]);

  const [fiche, setFiche] = useState(null);
  const [edition, setEdition] = useState(false);
  const [piecesJointes, setPiecesJointes] = useState([]);
  const [parLien, setParLien] = useState(false);
  const [consultee, setConsultee] = useState(null);
  const [aSupprimer, setASupprimer] = useState(null);
  const [aCloturer, setACloturer] = useState(null);
  const [occupe, setOccupe] = useState(false);
  const [envoi, setEnvoi] = useState(false);
  const [liaisons, setLiaisons] = useState([]);
  const [liaisonChoisie, setLiaisonChoisie] = useState('');
  const [recuesChoisies, setRecuesChoisies] = useState([]);

  const charger = useCallback(async () => {
    setChargement(true);
    try {
      const { data } = await axiosInstance.get('/tasks', {
        params: { expertise_id: expertiseId, page },
      });
      setTaches(data.results || data);
      setPages(data.total_pages || 1);
      setErreur('');
    } catch {
      setErreur("Les tâches n'ont pas pu être chargées.");
    } finally {
      setChargement(false);
    }
  }, [expertiseId, page]);

  useEffect(() => { charger(); }, [charger]);

  useEffect(() => {
    if (!expertiseId) return;
    Promise.all([
      axiosInstance.get('/templates/'),
      axiosInstance.get('/tasks_list_model/'),
      axiosInstance.get('/personne/', { params: { expertise_id: expertiseId } }),
      axiosInstance.get('/files', { params: { expertise_id: expertiseId } }),
    ]).then(([t, l, p, f]) => {
      setModeles(t.data.results || t.data);
      setListes(l.data.results || l.data);
      setContacts((p.data.results || p.data).filter((c) => c.mail));
      // Les documents sont dans `fichier`, les photos dans `file`.
      setPieces((f.data.results || f.data).filter((x) => x.fichier || x.file));
    }).catch(() => {});
    // Les dossiers reliés : seule l'organisation du dossier peut y envoyer.
    axiosInstance.get(`/expertises/${expertiseId}/liaisons/`)
      .then(({ data }) => setLiaisons(data.liaisons.filter((l) => !l.rompue_le)))
      .catch(() => setLiaisons([]));
  }, [expertiseId]);

  // La fenêtre de la GED prévient quand elle a modifié une tâche.
  useEffect(() => {
    const ecouter = (e) => {
      if (e.origin === window.location.origin
          && e.data?.type === 'TASK_UPDATED') charger();
    };
    window.addEventListener('message', ecouter);
    return () => window.removeEventListener('message', ecouter);
  }, [charger]);

  const visibles = taches
    .filter((t) => vue === 'toutes' || (
      ['complete', 'start'].includes(t.status) && t.hierarchy !== 'XFILE'))
    .filter((t) => voirDocuments || !vientDUnDocument(t));
  const documentsMasques = voirDocuments ? 0
    : taches.filter(vientDUnDocument).length;

  const basculerDocuments = (e) => {
    setVoirDocuments(e.target.checked);
    ecrireVoirDocuments(e.target.checked);
  };

  const corps = () => ({
    ...fiche,
    review_at: fiche.review_at || null,
    expertise: expertiseId,
  });

  const enregistrer = async (fermer = true) => {
    setOccupe(true);
    try {
      if (fiche.id) {
        await axiosInstance.put(`/tasks/${fiche.id}/`, corps());
      } else {
        await axiosInstance.post('/tasks/', corps());
      }
      setMessage('Tâche enregistrée');
      charger();
      if (fermer) setFiche(null);
    } catch {
      setErreur("La tâche n'a pas pu être enregistrée.");
    } finally {
      setOccupe(false);
    }
  };

  const envoyer = async () => {
    setEnvoi(true);
    try {
      let id = fiche.id;
      if (id) {
        await axiosInstance.put(`/tasks/${id}/`, corps());
      } else {
        const { data } = await axiosInstance.post('/tasks/', corps());
        id = data.id;
      }
      if (fiche.hierarchy === 'PARTAGE') {
        const { data: reponse } = await axiosInstance.post(
          `/tasks/${id}/envoyer-dossier/`, {
            liaison: liaisonChoisie,
            attachment_ids: piecesJointes,
            origine: window.location.origin,
          });
        setMessage(reponse.detail);
        setFiche(null);
        charger();
        return;
      }
      // Le serveur dit s'il a réparti l'envoi ou fait passer des pièces par lien.
      const { data: reponse } = await axiosInstance.post(
        `/tasks/${id}/send-task-email/`, {
          attachment_ids: piecesJointes,
          mode: parLien ? 'lien' : 'pieces',
          origine: window.location.origin,
        });
      setMessage(reponse?.courriels > 1 || reponse?.par_lien?.length
        ? reponse.detail : 'Courriel envoyé');
      setFiche(null);
      charger();
    } catch (error) {
      setErreur(error.response?.data?.detail
        || (fiche.hierarchy === 'PARTAGE' ? "Le partage n'a pas pu être envoyé."
          : "Le courriel n'a pas pu être envoyé."));
    } finally {
      setEnvoi(false);
    }
  };

  const cloturer = async () => {
    setOccupe(true);
    try {
      await axiosInstance.patch(`/tasks/${aCloturer.id}/`,
                                { status: 'complete' });
      setACloturer(null);
      setMessage('Tâche clôturée');
      charger();
    } catch {
      setErreur("La tâche n'a pas pu être clôturée.");
    } finally {
      setOccupe(false);
    }
  };

  const supprimer = async () => {
    setOccupe(true);
    try {
      await axiosInstance.delete(`/tasks/${aSupprimer.id}/`);
      setASupprimer(null);
      setMessage('Tâche supprimée');
      charger();
    } catch {
      setErreur("La tâche n'a pas pu être supprimée.");
    } finally {
      setOccupe(false);
    }
  };

  const appliquerListe = async () => {
    setOccupe(true);
    try {
      await axiosInstance.post('/tasks/add_from_model/', {
        task_list_model_id: listeChoisie, expertise_id: expertiseId,
      });
      setListeChoisie('');
      setMessage('Modèle appliqué');
      charger();
    } catch {
      setErreur("Le modèle n'a pas pu être appliqué.");
    } finally {
      setOccupe(false);
    }
  };

  const fermerLien = async (lien) => {
    setOccupe(true);
    try {
      await axiosInstance.delete(`/transferts/${lien.jeton}/gestion/`);
      setMessage('Lien désactivé');
      setConsultee((t) => t && {
        ...t,
        transferts: t.transferts.map((l) => (
          l.jeton === lien.jeton ? { ...l, valide: false } : l)),
      });
      charger();
    } catch {
      setErreur("Le lien n'a pas pu être désactivé.");
    } finally {
      setOccupe(false);
    }
  };

  // La zone « à reprendre » : chaque pièce reçue est reprise dans les
  // documents du dossier, ou écartée. Rien n'y entre sans ce geste.
  const traiterRecues = async (action) => {
    setOccupe(true);
    try {
      const { data } = await axiosInstance.post('/pieces-recues/', {
        ids: recuesChoisies, action,
      });
      setMessage(data.refus?.length ? data.refus.join(' ')
        : `${data.faites} pièce${data.faites > 1 ? 's' : ''} ${action === 'reprendre'
          ? `reprise${data.faites > 1 ? 's' : ''} dans les documents`
          : `écartée${data.faites > 1 ? 's' : ''}`}`);
      setRecuesChoisies([]);
      const { data: tache } = await axiosInstance.get(`/tasks/${consultee.id}/`);
      setConsultee(tache);
      charger();
    } catch {
      setErreur("Les pièces n'ont pas pu être traitées.");
    } finally {
      setOccupe(false);
    }
  };

  const consulter = (tache) => {
    setRecuesChoisies([]);
    setConsultee(tache);
  };

  const ouvrir = (tache) => {
    setLiaisonChoisie(liaisons.length === 1 ? liaisons[0].id : '');
    setPiecesJointes([]);
    setParLien(false);
    setEdition(Boolean(tache));
    setFiche(tache ? {
      ...VIDE, ...tache,
      review_at: (tache.review_at || '').split('T')[0],
    } : { ...VIDE });
  };

  return (
    <>
      <Stack spacing={3}>
        {erreur && <Alert severity="error"
                          onClose={() => setErreur('')}>{erreur}</Alert>}

        <Fiche titre="Suivi"
               explication="Ce qu'il reste à faire sur ce dossier, et les courriels envoyés."
               actions={(
                 <Button variant="contained" size="small"
                         startIcon={<BsPlusLg />} onClick={() => ouvrir(null)}>
                   Nouvelle tâche
                 </Button>
               )}>
          <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}
                 alignItems={{ md: 'center' }}>
            <TextField select label="Appliquer une liste type"
                       value={listeChoisie}
                       onChange={(e) => setListeChoisie(e.target.value)}
                       sx={{ maxWidth: { md: 380 } }}
                       helperText="Elle ajoute ses tâches au dossier.">
              <MenuItem value=""><em>Choisir…</em></MenuItem>
              {listes.map((liste) => (
                <MenuItem key={liste.id} value={liste.id}>{liste.title}</MenuItem>
              ))}
            </TextField>
            <Button variant="outlined" onClick={appliquerListe}
                    disabled={!listeChoisie || occupe}
                    sx={{ alignSelf: { xs: 'flex-start', md: 'center' } }}>
              Appliquer
            </Button>
          </Stack>
        </Fiche>

        <Fiche sx={{ p: 0 }}>
          <Stack direction="row" spacing={2} alignItems="center"
                 justifyContent="space-between"
                 sx={{ px: 2, pt: 2, pb: 1, flexWrap: 'wrap' }}>
            <Typography variant="body2" color="text.secondary">
              {visibles.length} tâche{visibles.length > 1 ? 's' : ''}
            </Typography>
            <Stack direction="row" spacing={2} alignItems="center"
                   flexWrap="wrap" useFlexGap>
              <FormControlLabel
                control={<Switch size="small" checked={voirDocuments}
                                 onChange={basculerDocuments} />}
                label={<Typography variant="body2">Tâches des documents</Typography>}
              />
              <ToggleButtonGroup size="small" exclusive value={vue}
                                 onChange={(_, v) => v && setVue(v)}>
                <ToggleButton value="utiles">En cours et terminées</ToggleButton>
                <ToggleButton value="toutes">Toutes</ToggleButton>
              </ToggleButtonGroup>
            </Stack>
          </Stack>

          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Titre</TableCell>
                  <TableCell>Destinataire</TableCell>
                  <TableCell>Échéance</TableCell>
                  <TableCell>Statut</TableCell>
                  <TableCell align="right">&nbsp;</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {chargement && [...Array(4)].map((_, i) => (
                  <TableRow key={i}>
                    <TableCell colSpan={5}><Skeleton height={28} /></TableCell>
                  </TableRow>
                ))}

                {!chargement && visibles.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={5} sx={{ py: 5, textAlign: 'center' }}>
                      <Typography variant="body2" color="text.secondary"
                                  sx={{ mb: 2 }}>
                        {documentsMasques > 0
                          ? "Seules des tâches de documents restent : elles sont masquées."
                          : vue === 'utiles' && taches.length > 0
                            ? "Aucune tâche en cours. Choisissez « Toutes » pour voir le reste."
                            : 'Aucune tâche sur ce dossier.'}
                      </Typography>
                      <Button size="small" onClick={() => ouvrir(null)}>
                        Créer une tâche
                      </Button>
                    </TableCell>
                  </TableRow>
                )}

                {visibles.map((tache) => (
                  <TableRow key={tache.id} hover>
                    <TableCell>
                      <Stack direction="row" spacing={1} alignItems="center"
                             flexWrap="wrap" useFlexGap>
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                          {tache.title}
                        </Typography>
                        {tache.hierarchy === 'EMAIL' && (
                          <Chip size="small" color="warning" label="courriel" />
                        )}
                        {tache.hierarchy === 'XMAIL' && (
                          <Chip size="small" color="success" label="envoyé" />
                        )}
                        {tache.hierarchy === 'PARTAGE' && (
                          <Chip size="small" color="warning" label="partage" />
                        )}
                        {tache.hierarchy === 'XPARTAGE' && (
                          <Chip size="small" color="success" label="partagé" />
                        )}
                        {tache.hierarchy === 'XRECU' && (
                          <Chip size="small" color="info" label="reçu"
                                onClick={() => consulter(tache)} />
                        )}
                        {aReprendre(tache) > 0 && (
                          <Chip size="small" color="warning" variant="outlined"
                                label={`${aReprendre(tache)} à reprendre`}
                                onClick={() => consulter(tache)} />
                        )}
                        {(tache.transferts || []).slice(0, 1).map((lien) => (
                          <Chip key={lien.jeton} size="small" variant="outlined"
                                color={etatLien(lien).couleur}
                                label={etatLien(lien).libelle}
                                onClick={() => consulter(tache)} />
                        ))}
                        {vientDUnDocument(tache) && (
                          <Chip size="small" variant="outlined" label="document" />
                        )}
                        {tache.public && (
                          <Chip size="small" variant="outlined" label="partagée" />
                        )}
                      </Stack>
                    </TableCell>
                    <TableCell>
                      {tache.receiver || <Box component="span"
                                              sx={{ color: 'text.disabled' }}>—</Box>}
                    </TableCell>
                    <TableCell sx={{ whiteSpace: 'nowrap' }}>
                      {dateCourte(tache.review_at)
                        || <Box component="span"
                                sx={{ color: 'text.disabled' }}>—</Box>}
                    </TableCell>
                    <TableCell>
                      <Chip size="small"
                            color={statut(tache.status)?.couleur || 'default'}
                            label={statut(tache.status)?.libelle || tache.status} />
                    </TableCell>
                    <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                      <Tooltip title="Consulter">
                        <IconButton size="small"
                                    onClick={() => consulter(tache)}>
                          <BsEye />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Modifier">
                        <IconButton size="small" onClick={() => ouvrir(tache)}>
                          <BsPencil />
                        </IconButton>
                      </Tooltip>
                      {tache.status !== 'complete' && (
                        <Tooltip title="Clôturer">
                          <IconButton size="small"
                                      onClick={() => setACloturer(tache)}>
                            <BsCheck2 />
                          </IconButton>
                        </Tooltip>
                      )}
                      <Tooltip title="Supprimer">
                        <IconButton size="small"
                                    onClick={() => setASupprimer(tache)}>
                          <BsTrash />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>

          {pages > 1 && (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
              <Pagination count={pages} page={page}
                          onChange={(_, v) => setPage(v)} />
            </Box>
          )}
        </Fiche>
      </Stack>

      <TaskDialog
        ouvert={fiche !== null}
        onFermer={() => setFiche(null)}
        tache={fiche}
        setTache={setFiche}
        onEnregistrer={enregistrer}
        onEnvoyer={envoyer}
        modeles={modeles}
        contacts={contacts}
        pieces={pieces}
        piecesJointes={piecesJointes}
        onBasculerPiece={(id) => setPiecesJointes((etat) => (
          etat.includes(id) ? etat.filter((x) => x !== id) : [...etat, id]))}
        onChoisirPieces={(ids, coche) => setPiecesJointes((etat) => (coche
          ? [...new Set([...etat, ...ids])]
          : etat.filter((x) => !ids.includes(x))))}
        parLien={parLien}
        setParLien={setParLien}
        edition={edition}
        occupe={occupe}
        envoi={envoi}
        liaisons={liaisons}
        liaisonChoisie={liaisonChoisie}
        setLiaisonChoisie={setLiaisonChoisie}
      />

      <ThemeProvider theme={themeExpert}>
        <Dialog open={consultee !== null} onClose={() => setConsultee(null)}
                fullWidth maxWidth="md">
          <DialogTitle>{consultee?.title}</DialogTitle>
          <DialogContent dividers>
            <Stack spacing={2}>
              <Stack direction="row" spacing={3} flexWrap="wrap" useFlexGap>
                <Box>
                  <Typography variant="caption" color="text.secondary"
                              component="div">Statut</Typography>
                  <Chip size="small"
                        color={statut(consultee?.status)?.couleur || 'default'}
                        label={statut(consultee?.status)?.libelle || '—'} />
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary"
                              component="div">Destinataire</Typography>
                  <Typography variant="body2">
                    {consultee?.receiver || '—'}
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary"
                              component="div">Échéance</Typography>
                  <Typography variant="body2">
                    {dateCourte(consultee?.review_at) || '—'}
                  </Typography>
                </Box>
              </Stack>

              {(consultee?.envois_dossier || []).map((e) => (
                <Alert key={e.envoye_le} severity="success" icon={false}>
                  <Typography variant="body2">
                    Envoyé le {dateCourte(e.envoye_le)} à {e.organisation}
                    {e.reference ? `, dossier ${e.reference}` : ''}
                    {e.pieces ? `, avec ${e.pieces} pièce${e.pieces > 1 ? 's' : ''}` : ''}.
                  </Typography>
                </Alert>
              ))}

              {consultee?.envoi_recu && (
                <Box sx={{ border: '1px solid', borderColor: 'divider',
                           borderRadius: 1, p: 2 }}>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    Reçu le {dateCourte(consultee.envoi_recu.envoye_le)} de{' '}
                    {consultee.envoi_recu.organisation}
                    {consultee.envoi_recu.reference
                      ? `, dossier ${consultee.envoi_recu.reference}` : ''}
                  </Typography>
                  {consultee.envoi_recu.pieces.length === 0 ? (
                    <Typography variant="body2" color="text.secondary">
                      Aucune pièce jointe.
                    </Typography>
                  ) : (
                    <>
                      <Typography variant="caption" color="text.secondary"
                                  component="div" sx={{ mb: 1 }}>
                        Ces pièces ne sont pas encore dans vos documents.
                        Reprenez celles que vous gardez ; écarter une pièce ne
                        l'efface pas de l'envoi.
                      </Typography>
                      <Stack spacing={0.5}>
                        {consultee.envoi_recu.pieces.map((p) => (
                          <Stack key={p.id} direction="row" spacing={1}
                                 alignItems="center">
                            <Checkbox size="small"
                                      disabled={p.statut !== 'à reprendre'}
                                      checked={recuesChoisies.includes(p.id)}
                                      onChange={() => setRecuesChoisies((x) => (
                                        x.includes(p.id) ? x.filter((i) => i !== p.id)
                                          : [...x, p.id]))} />
                            {p.est_photo && (
                              <Box component="img" src={p.url} alt="" loading="lazy"
                                   sx={{ width: 48, height: 48, objectFit: 'cover',
                                         borderRadius: 0.5 }} />
                            )}
                            <Box component="a" href={p.url} target="_blank"
                                 rel="noopener noreferrer"
                                 sx={{ color: 'primary.main', flexGrow: 1,
                                       minWidth: 0, wordBreak: 'break-word' }}>
                              {p.nom}
                            </Box>
                            <Chip size="small" variant="outlined" label={p.statut}
                                  color={COULEUR_PIECE[p.statut] || 'default'} />
                          </Stack>
                        ))}
                      </Stack>
                      {aReprendre(consultee) > 0 && (
                        <Stack direction="row" spacing={1} sx={{ mt: 1.5 }}
                               flexWrap="wrap" useFlexGap>
                          <Button size="small" onClick={() => setRecuesChoisies(
                            consultee.envoi_recu.pieces
                              .filter((p) => p.statut === 'à reprendre').map((p) => p.id))}>
                            Tout sélectionner
                          </Button>
                          <Button size="small" variant="contained"
                                  disabled={occupe || recuesChoisies.length === 0}
                                  onClick={() => traiterRecues('reprendre')}>
                            Reprendre dans mes documents
                          </Button>
                          <Button size="small" color="inherit"
                                  disabled={occupe || recuesChoisies.length === 0}
                                  onClick={() => traiterRecues('ecarter')}>
                            Écarter
                          </Button>
                        </Stack>
                      )}
                    </>
                  )}
                </Box>
              )}

              {(consultee?.transferts || []).map((lien) => (
                <Alert key={lien.jeton} severity={lien.valide ? 'info' : 'warning'}
                       icon={false}
                       action={lien.valide && (
                         <Button size="small" color="inherit" disabled={occupe}
                                 onClick={() => fermerLien(lien)}>
                           Désactiver
                         </Button>
                       )}>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    Lien de téléchargement — {lien.pieces} pièce{lien.pieces > 1 ? 's' : ''}
                  </Typography>
                  <Typography variant="body2">
                    {lien.telechargements === 0
                      ? 'Pas encore téléchargé.'
                      : `Téléchargé ${lien.telechargements} fois, dernière fois le ${dateCourte(lien.dernier_telechargement)}.`}
                    {' '}
                    {lien.valide
                      ? `Valable jusqu'au ${dateCourte(lien.expire_le)}.`
                      : 'Lien fermé : il ne donne plus accès aux pièces.'}
                  </Typography>
                </Alert>
              ))}

              <Box sx={{ border: '1px solid', borderColor: 'divider',
                         borderRadius: 1, p: 2,
                         '& img': { maxWidth: '100%' } }}
                   dangerouslySetInnerHTML={{
                     __html: consultee?.comment || '<p>—</p>' }} />
            </Stack>
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 2 }}>
            <Button onClick={() => setConsultee(null)}>Fermer</Button>
          </DialogActions>
        </Dialog>

        <Dialog open={aCloturer !== null} onClose={() => setACloturer(null)}>
          <DialogTitle>Clôturer cette tâche ?</DialogTitle>
          <DialogContent>
            <Typography variant="body2">
              « {aCloturer?.title} » passera en terminée. Elle reste au dossier.
            </Typography>
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 2 }}>
            <Button onClick={() => setACloturer(null)} disabled={occupe}>
              Annuler
            </Button>
            <Button onClick={cloturer} variant="contained" disabled={occupe}>
              Clôturer
            </Button>
          </DialogActions>
        </Dialog>

        <Dialog open={aSupprimer !== null} onClose={() => setASupprimer(null)}>
          <DialogTitle>Supprimer cette tâche ?</DialogTitle>
          <DialogContent>
            <Typography variant="body2">
              « {aSupprimer?.title} » sera retirée du dossier. C'est définitif —
              pour garder la trace, clôturez-la plutôt.
            </Typography>
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 2 }}>
            <Button onClick={() => setASupprimer(null)} disabled={occupe}>
              Annuler
            </Button>
            <Button onClick={supprimer} color="error" variant="contained"
                    disabled={occupe}>
              Supprimer
            </Button>
          </DialogActions>
        </Dialog>
      </ThemeProvider>

      <Snackbar open={Boolean(message)} autoHideDuration={message.length > 40 ? 10000 : 3000}
                onClose={() => setMessage('')} message={message}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }} />
    </>
  );
}

export default TaskView;
